import React, { useState } from 'react';
import { MessageSquare, Send, Loader } from 'lucide-react';

const FormRenderer = ({ form, onSubmit, isPreview = false }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!form) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Form not found</p>
      </div>
    );
  }

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    form.fields.forEach(field => {
      if (field.required && !formData[field.id]) {
        newErrors[field.id] = `${field.label} is required`;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (isPreview) {
      alert('This is a preview. In the actual form, data would be sent to WhatsApp.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Format the message for WhatsApp
      const formattedData = formatDataForWhatsApp(form, formData);
      
      // If onSubmit is provided, call it
      if (onSubmit) {
        await onSubmit(formData, formattedData);
      }
      
      // Get the WhatsApp URL
      const whatsappUrl = generateWhatsAppUrl(form.settings.whatsappNumber, formattedData);
      
      // Need to handle mobile Safari differently
      const isMobile = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isSafari = /Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent);
      
      if (isMobile && isSafari) {
        // For Safari on iOS, use location.href instead of window.open
        // Show the submission successful message first
        setSubmitted(true);
        // Then redirect after a short delay
        setTimeout(() => {
          window.location.href = whatsappUrl;
        }, 500);
      } else {
        // For other browsers, open in a new tab
        window.open(whatsappUrl, '_blank');
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format form data for WhatsApp message
  const formatDataForWhatsApp = (form, data) => {
    let message = form.settings.messageTemplate || '';
    
    // Replace {{formData}} with all form fields
    if (message.includes('{{formData}}')) {
      const formDataText = form.fields.map(field => {
        let value = data[field.id] || 'Not provided';
        
        // Format checkbox values (arrays) as comma-separated list
        if (field.type === 'checkbox' && Array.isArray(value)) {
          value = value.join(', ');
        }
        
        return `${field.label}: ${value}`;
      }).join('\n');
      
      message = message.replace('{{formData}}', formDataText);
    }
    
    // Replace individual field placeholders
    form.fields.forEach(field => {
      const placeholder = `{{${field.id}}}`;
      if (message.includes(placeholder)) {
        let value = data[field.id] || 'Not provided';
        
        // Format checkbox values (arrays) as comma-separated list
        if (field.type === 'checkbox' && Array.isArray(value)) {
          value = value.join(', ');
        }
        
        message = message.replace(placeholder, value);
      }
    });
    
    // Add branding footer
    message += '\n\n_Powered by KangleiCode Tools_\nwww.kangleicode.in & www.tools.kangleicode.in';
    
    return message;
  };

  // Generate WhatsApp URL
  const generateWhatsAppUrl = (number, message) => {
    const cleanNumber = number.replace(/[^0-9]/g, '');
    
    // iOS Safari often handles wa.me links better than api.whatsapp.com links
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      const encodedMessage = encodeURIComponent(message);
      return `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
    } else {
      const encodedMessage = encodeURIComponent(message);
      return `https://api.whatsapp.com/send/?phone=${cleanNumber}&text=${encodedMessage}&type=phone_number&app_absent=0`;
    }
  };

  // Apply theme styles
  const getThemeStyles = () => {
    switch (form.settings?.theme || 'default') {
      case 'minimal':
        return {
          container: 'bg-white',
          header: 'border-b border-gray-100',
          button: 'bg-black text-white hover:bg-gray-800',
          input: 'border-gray-200 focus:border-black focus:ring-black',
          fieldContainer: 'mb-6 transition-all duration-300 hover:shadow-md p-4 rounded-lg border border-transparent hover:border-gray-100'
        };
      case 'colorful':
        return {
          container: 'bg-gradient-to-br from-indigo-50 to-purple-50',
          header: 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white',
          button: 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700',
          input: 'border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500',
          fieldContainer: 'mb-6 transition-all duration-300 hover:shadow-md p-4 rounded-lg bg-white bg-opacity-70 hover:bg-opacity-100'
        };
      case 'dark':
        return {
          container: 'bg-gray-900 text-white',
          header: 'border-b border-gray-700',
          button: 'bg-indigo-600 text-white hover:bg-indigo-700',
          input: 'bg-gray-800 border-gray-700 text-white focus:border-indigo-500 focus:ring-indigo-500',
          fieldContainer: 'mb-6 transition-all duration-300 hover:shadow-md p-4 rounded-lg border border-gray-800 hover:border-gray-700 bg-gray-800 bg-opacity-50 hover:bg-opacity-70'
        };
      default:
        return {
          container: 'bg-white shadow-xl border border-gray-100',
          header: 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white',
          button: 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1',
          input: 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 transition-all duration-300 focus:shadow-md',
          fieldContainer: 'mb-6 transition-all duration-300 hover:shadow-md p-4 rounded-lg border border-transparent hover:border-indigo-100'
        };
    }
  };

  const theme = getThemeStyles();

  // Render thank you message after submission
  if (submitted) {
    // Generate WhatsApp URL for the success page button
    const whatsappUrl = form.settings?.whatsappNumber ? 
      generateWhatsAppUrl(form.settings.whatsappNumber, formatDataForWhatsApp(form, formData)) : '';
      
    return (
      <div className={`rounded-lg shadow-lg overflow-hidden ${theme.container}`}>
        <div className={`px-6 py-4 ${theme.header}`}>
          <h2 className="text-xl font-bold">Thank You!</h2>
        </div>
        <div className="p-6">
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <h3 className="mt-2 text-lg font-medium">Form Submitted Successfully</h3>
            <p className="mt-1 text-sm text-gray-500">
              Your message has been sent to WhatsApp. Thank you for your submission!
            </p>
            
            {/* WhatsApp direct link for mobile Safari users */}
            <div className="mt-4">
              <a 
                href={whatsappUrl}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm bg-green-600 text-white hover:bg-green-700`}
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Open in WhatsApp
              </a>
            </div>
            
            {form.settings?.redirectUrl && (
              <div className="mt-6">
                <a
                  href={form.settings.redirectUrl}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm ${theme.button}`}
                >
                  Continue
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render form
  return (
    <div className={`rounded-lg shadow-lg overflow-hidden ${theme.container}`}>
      <div className={`px-6 py-4 ${theme.header}`}>
        <div className="flex items-center">
          <MessageSquare className="h-6 w-6 mr-2" />
          <h2 className="text-xl font-bold">{form.name}</h2>
        </div>
        {form.description && (
          <p className="mt-1 text-sm">{form.description}</p>
        )}
      </div>
      <div className="p-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {form.fields.map((field) => (
              <div key={field.id} className={theme.fieldContainer}>
                <label
                  htmlFor={field.id}
                  className="block text-sm font-medium mb-1"
                >
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                
                {renderField(field, formData, handleChange, theme.input, setFormData, errors, setErrors)}
                
                {errors[field.id] && (
                  <p className="mt-1 text-sm text-red-600">{errors[field.id]}</p>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-lg shadow-lg text-base font-medium ${theme.button} focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-indigo-500 transform transition-all duration-300 hover:scale-105 active:scale-95`}
            >
              {isSubmitting ? (
                <>
                  <Loader className="animate-spin -ml-1 mr-3 h-5 w-5" />
                  <span className="animate-pulse">Submitting...</span>
                </>
              ) : (
                <>
                  <Send className="-ml-1 mr-3 h-5 w-5" />
                  Submit Form
                </>
              )}
            </button>
            <p className="text-center text-xs text-gray-500 mt-3">
            Your response will be sent via WhatsApp. Please ensure WhatsApp is installed. Thank you!
            </p>
          </div>
        </form>
        
        {/* Branding Footer */}
        <div className="mt-8 pt-4 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            Powered by <a href="https://www.kangleicode.in" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-500">KangleiCode Tools</a>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            <a href="https://www.kangleicode.in" target="_blank" rel="noopener noreferrer" className="hover:text-gray-500">www.kangleicode.in</a> &amp; <a href="https://tools.kangleicode.in" target="_blank" rel="noopener noreferrer" className="hover:text-gray-500">tools.kangleicode.in</a>
          </p>
        </div>
      </div>
    </div>
  );
};

// Render different field types
const renderField = (field, formData, handleChange, inputThemeClass, setFormData, errors, setErrors) => {
  const value = formData[field.id] || '';
  
  switch (field.type) {
    case 'textarea':
      return (
        <div className="relative">
          <textarea
            id={field.id}
            name={field.id}
            value={value}
            onChange={(e) => {
              setFormData({
                ...formData,
                [field.id]: e.target.value
              });
              
              // Clear error for this field
              if (errors[field.id]) {
                setErrors({
                  ...errors,
                  [field.id]: null
                });
              }
            }}
            placeholder={field.placeholder}
            required={field.required}
            rows="4"
            className={`shadow-sm block w-full sm:text-sm rounded-md transition-all duration-200 focus:ring-2 focus:ring-indigo-500 ${value ? 'font-medium text-gray-900' : 'text-gray-500'} ${inputThemeClass}`}
          />
          {value && (
            <div className="absolute right-3 top-3">
              <svg className="h-4 w-4 text-indigo-600 opacity-70" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
      );
    
    case 'select':
      return (
        <div className="relative">
          <select
            id={field.id}
            name={field.id}
            value={value}
            onChange={(e) => {
              setFormData({
                ...formData,
                [field.id]: e.target.value
              });
              
              // Clear error for this field
              if (errors[field.id]) {
                setErrors({
                  ...errors,
                  [field.id]: null
                });
              }
            }}
            required={field.required}
            className={`appearance-none block w-full pl-3 pr-10 py-2 text-base rounded-md focus:ring-2 focus:ring-indigo-500 ${value ? 'font-medium text-gray-900' : 'text-gray-500'} ${inputThemeClass}`}
          >
            <option value="">{field.placeholder || 'Select an option'}</option>
            {field.options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      );
    
    case 'radio':
      return (
        <div className="mt-1 space-y-2">
          {field.options.map((option, index) => {
            const isSelected = value === option;
            return (
              <div key={index} className="flex items-center">
                <div 
                  className={`relative flex items-center justify-center h-5 w-5 rounded-full border ${isSelected 
                    ? 'bg-indigo-600 border-indigo-600' 
                    : 'bg-white border-gray-300'} 
                    transition-colors duration-200 ease-in-out cursor-pointer`}
                  onClick={() => {
                    setFormData({
                      ...formData,
                      [field.id]: option
                    });
                    
                    // Clear error for this field
                    if (errors[field.id]) {
                      setErrors({
                        ...errors,
                        [field.id]: null
                      });
                    }
                  }}
                >
                  {isSelected && (
                    <div className="h-2 w-2 rounded-full bg-white"></div>
                  )}
                  <input
                    id={`${field.id}-${index}`}
                    name={field.id}
                    type="radio"
                    value={option}
                    checked={isSelected}
                    onChange={() => {}} // Handled by the onClick handler above
                    className="absolute opacity-0 h-0 w-0" // Hide the actual radio but keep it accessible
                  />
                </div>
                <label 
                  htmlFor={`${field.id}-${index}`} 
                  className={`ml-3 block text-sm ${isSelected ? 'font-medium' : ''}`}
                >
                  {option}
                </label>
              </div>
            );
          })}
        </div>
      );
    
    case 'checkbox':
      return (
        <div className="mt-1 space-y-2">
          {field.options.map((option, index) => {
            const isChecked = Array.isArray(formData[field.id]) && formData[field.id].includes(option);
            return (
              <div key={index} className="flex items-center">
                <div 
                  className={`relative flex items-center justify-center h-5 w-5 rounded border ${isChecked 
                    ? 'bg-indigo-600 border-indigo-600' 
                    : 'bg-white border-gray-300'} 
                    transition-colors duration-200 ease-in-out cursor-pointer`}
                  onClick={() => {
                    const currentValues = Array.isArray(formData[field.id]) ? [...formData[field.id]] : [];
                    
                    if (isChecked) {
                      // Remove the value if already checked
                      setFormData({
                        ...formData,
                        [field.id]: currentValues.filter(val => val !== option)
                      });
                    } else {
                      // Add the value if not checked
                      setFormData({
                        ...formData,
                        [field.id]: [...currentValues, option]
                      });
                    }
                    
                    // Clear error for this field
                    if (errors[field.id]) {
                      setErrors({
                        ...errors,
                        [field.id]: null
                      });
                    }
                  }}
                >
                  {isChecked && (
                    <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  <input
                    id={`${field.id}-${index}`}
                    name={field.id}
                    type="checkbox"
                    value={option}
                    checked={isChecked}
                    onChange={() => {}} // Handled by the onClick handler above
                    className="absolute opacity-0 h-0 w-0" // Hide the actual checkbox but keep it accessible
                  />
                </div>
                <label 
                  htmlFor={`${field.id}-${index}`} 
                  className={`ml-3 block text-sm ${isChecked ? 'font-medium' : ''}`}
                >
                  {option}
                </label>
              </div>
            );
          })}
        </div>
      );
    
    default:
      return (
        <div className="relative">
          <input
            type={field.type}
            id={field.id}
            name={field.id}
            value={value}
            onChange={(e) => {
              setFormData({
                ...formData,
                [field.id]: e.target.value
              });
              
              // Clear error for this field
              if (errors[field.id]) {
                setErrors({
                  ...errors,
                  [field.id]: null
                });
              }
            }}
            placeholder={field.placeholder}
            required={field.required}
            className={`shadow-sm block w-full sm:text-sm rounded-md transition-all duration-200 focus:ring-2 focus:ring-indigo-500 ${value ? 'font-medium text-gray-900' : 'text-gray-500'} ${inputThemeClass}`}
          />
          {value && field.type === 'date' && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <svg className="h-4 w-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
      );
  }
};

export default FormRenderer;
